import { faFilePdf, faCommentDollar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CerebroDrawer, CerebroHeading, CerebroIcon, CerebroText, LenderStoreDot } from '../../shared/components';
import { DataRoomTeaser } from '../../shared/models';
import { DateUtils, EnumUtils } from '../../shared/utils';
import { useBankDealProfileFromContactQuery } from '../../shared/hooks';
import { BankDealProfileLayout } from '../../shared/components/BankDealProfileLayout';

interface TeaserDetailProps {
	teaser?: DataRoomTeaser | undefined | null;
	close: () => void;
}

export const TeaserDetail: React.FC<TeaserDetailProps> = ({ teaser, close }) => {
	const dealProfileQuery = useBankDealProfileFromContactQuery(teaser?.bankGuid, teaser?.bankContactGuid, {
		enabled: teaser != null,
		retry: false,
	});
	const isPending = teaser?.decisionStatus == 'Unsent' || teaser?.decisionStatus == 'Sent';
	const isFlagged = teaser?.lenderScoreFlag ?? false;

	return (
		<CerebroDrawer open={teaser != null} onClose={() => close()} PaperProps={{ sx: { width: '500px' } }}>
			<CerebroHeading size="h1" gutterBottom>
				{teaser?.firstName} {teaser?.lastName}
			</CerebroHeading>
			<CerebroText gutterBottom>
				{teaser?.emailAddress} <br />
				<br />
				{teaser?.bankName}{' '}
				{teaser?.bankHasNda && (
					<CerebroIcon
						sx={theme => ({
							color: theme.palette.success.main,
						})}
						title={`Bank has NDA`}
					>
						<FontAwesomeIcon icon={faFilePdf} />
					</CerebroIcon>
				)}
			</CerebroText>
			<CerebroText>
				{EnumUtils.GetTier1BankTypeDescription(teaser?.tier1Type)} (
				{EnumUtils.GetTier2BankTypeDescription(teaser?.tier2Type)})
			</CerebroText>

			{teaser != null &&
				teaser.finderFeeAgreements &&
				teaser.finderFeeAgreements.filter(fee => fee != 'None').length > 0 && (
					<>
						<CerebroHeading size="h3" sx={{ marginTop: 2 }}>
							Finder Fee Agreements
						</CerebroHeading>
						{teaser.finderFeeAgreements
							.filter(fee => fee != 'None')
							.map(fee => (
								<CerebroText key={fee}>
									{fee}{' '}
									<CerebroIcon
										sx={theme => ({
											color: theme.palette.success.main,
										})}
										title={`Has finder fee agreement(s)`}
									>
										<FontAwesomeIcon icon={faCommentDollar} />
									</CerebroIcon>
								</CerebroText>
							))}
					</>
				)}

			<CerebroHeading size="h3" sx={{ marginTop: 2 }}>
				Lender Score
			</CerebroHeading>
			<CerebroText gutterBottom>
				<LenderStoreDot score={teaser?.score} isFlagged={isFlagged} sx={{ marginRight: 0.5 }} /> {teaser?.score}
			</CerebroText>

			<CerebroHeading size="h3" sx={{ marginTop: 2 }}>
				Tranche
			</CerebroHeading>
			<CerebroText gutterBottom>{EnumUtils.GetStructureTrancheDescription(teaser?.structureType)}</CerebroText>

			<CerebroHeading size="h3" sx={{ marginTop: 2 }}>
				Status
			</CerebroHeading>
			<CerebroText gutterBottom>{teaser?.decisionStatus}</CerebroText>

			<CerebroHeading size="h3" sx={{ marginTop: 2 }}>
				Teaser Sent On
			</CerebroHeading>
			{teaser?.sentOn ? (
				<CerebroText gutterBottom>{DateUtils.FormatStringAsShortDate(teaser.sentOn.toString())}</CerebroText>
			) : (
				<CerebroText gutterBottom>Not sent</CerebroText>
			)}

			{isPending && teaser?.decisionOn && (
				<>
					<CerebroHeading size="h3" sx={{ marginTop: 2 }}>
						{teaser?.decisionStatus} On
					</CerebroHeading>
					<CerebroText gutterBottom>
						{DateUtils.FormatStringAsShortDate(teaser.decisionOn.toString())}
					</CerebroText>
				</>
			)}

			{teaser?.decisionStatus == 'Declined' && (
				<>
					<CerebroHeading size="h3" sx={{ marginTop: 2 }}>
						Decline Reasons
					</CerebroHeading>
					{teaser?.lenderDeclineReasons &&
						teaser.lenderDeclineReasons.length > 0 &&
						teaser.lenderDeclineReasons.map(reason => <CerebroText key={reason}>{reason}</CerebroText>)}
					{teaser?.otherDeclineReason && <CerebroText>{teaser.otherDeclineReason}</CerebroText>}

					<CerebroHeading size="h3" sx={{ marginTop: 2 }}>
						Reason Details
					</CerebroHeading>
					{teaser?.reasonDetail}

					<CerebroHeading size="h3" sx={{ marginTop: 2 }}>
						Re-Engage Point
					</CerebroHeading>
					{teaser?.reEngagePoint}
				</>
			)}

			<BankDealProfileLayout dealProfileQuery={dealProfileQuery} />
		</CerebroDrawer>
	);
};
